import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Loader2, MapPin, Plus, X } from 'lucide-react'

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { COUNTRIES } from '../../data/countries'
import { useDebouncedValue } from '../../hooks/useDebouncedValue'
import { LocationsApi } from '@/services/targeting/targetingLookupApi'
import type { MunicipalityRef, NamedLocationRef } from '@/services/targeting/targeting'
import { NegateToggle } from './NegateToggle'
import { FieldLabel } from '../FieldLabel'
import { LocationBrowser } from './LocationBrowser'

/** Stands in for "no country restriction" — a Select item needs a value. */
const ANY_COUNTRY = '__any__'

export interface LocationTargetValue {
  locations: NamedLocationRef[]
  /** Whole municipalities, targeted as one indivisible area. */
  municipalities: MunicipalityRef[]
  negated: boolean
}

export interface LocationTargetEditorProps {
  value: LocationTargetValue
  onChange: (value: LocationTargetValue) => void
}

/** Targets where the user is, by looked-up location or by whole municipality. */
export function LocationTargetEditor({ value, onChange }: LocationTargetEditorProps) {
  const { t } = useTranslation()
  const [term, setTerm] = useState('')
  /** One location type out of the search's breakdown, or all of them. */
  const [typeFilter, setTypeFilter] = useState('')
  const [country, setCountry] = useState('')
  const [municipalityFilter, setMunicipalityFilter] = useState('')
  const [confirmAddAll, setConfirmAddAll] = useState(false)
  const debouncedTerm = useDebouncedValue(term)

  const { data: locations, isFetching } = useQuery({
    queryKey: ['targeting-locations', debouncedTerm, country, typeFilter],
    queryFn: () =>
      LocationsApi.search({
        q: debouncedTerm,
        countryId: country,
        types: typeFilter || undefined,
        pageSize: 250,
      }),
    enabled: debouncedTerm.length > 0,
  })

  const searching = debouncedTerm.length > 0

  const { data: municipalities = [], isFetching: loadingMunicipalities } = useQuery({
    queryKey: ['targeting-municipalities', country],
    queryFn: () => LocationsApi.municipalities(country),
    enabled: !!country,
    staleTime: 60 * 60 * 1000,
  })

  const targetedLocations = new Set(value.locations.map((l) => l.id))
  const targetedMunicipalities = new Set(value.municipalities.map((m) => m.id))

  const addLocation = (location: NamedLocationRef) => {
    if (targetedLocations.has(location.id)) return
    onChange({ ...value, locations: [...value.locations, location] })
  }

  /** Every match not already targeted, added in one go. */
  const addAllResults = () => {
    if (untargeted.length === 0) return
    onChange({ ...value, locations: [...value.locations, ...untargeted] })
    setConfirmAddAll(false)
  }

  const toggleLocation = (location: NamedLocationRef, selected: boolean) =>
    selected
      ? addLocation(location)
      : onChange({ ...value, locations: value.locations.filter((l) => l.id !== location.id) })

  /** The matches a bulk add would bring in — what the confirmation lists. */
  const untargeted = (locations?.results ?? []).filter((l) => !targetedLocations.has(l.id))

  const matchingMunicipalities = municipalities.filter((m) =>
    (m.name ?? '').toLowerCase().includes(municipalityFilter.trim().toLowerCase())
  )

  return (
    <div className="flex flex-col gap-4">
      <NegateToggle
        negated={value.negated}
        what={t('targeting.types.namedLocation.plural').toLowerCase()}
        onChange={(negated) => onChange({ ...value, negated })}
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1.5">
          <FieldLabel className="text-xs font-medium">{t('targeting.location.country')}</FieldLabel>
          <Select
            value={country || ANY_COUNTRY}
            onValueChange={(v) => {
              setCountry(v === ANY_COUNTRY ? '' : v)
              setMunicipalityFilter('')
            }}
          >
            <SelectTrigger size="sm" className="w-56">
              <SelectValue>
                {country
                  ? (COUNTRIES.find((c) => c.value === country)?.label ?? country)
                  : t('targeting.location.anyCountry')}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY_COUNTRY}>{t('targeting.location.anyCountry')}</SelectItem>
              {COUNTRIES.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex min-w-64 flex-1 flex-col gap-1.5">
          <FieldLabel className="text-xs font-medium">{t('targeting.location.search')}</FieldLabel>
          <Input
            value={term}
            onChange={(e) => {
              setTerm(e.target.value)
              // The breakdown belongs to one search; a new term gets a new one.
              setTypeFilter('')
            }}
            placeholder={t('targeting.location.searchPlaceholder')}
          />
        </div>
      </div>

      {!searching && (
        <div className="flex flex-col gap-2">
          <FieldLabel className="text-xs font-medium" tooltip={t('targeting.location.browseHelp')}>
            {t('targeting.location.browse')}
          </FieldLabel>
          <LocationBrowser selected={value.locations} onToggle={toggleLocation} />
        </div>
      )}

      {searching && (
        <div className="flex flex-col gap-2 rounded-md border p-3">
          {/* The index's own breakdown of the matches, so a search that spans
              countries, regions and postcodes can be cut to one kind. */}
          {!!locations && locations.typeCounts.length > 1 && (
            <div className="flex flex-wrap items-center gap-1.5">
              <Badge
                asChild
                variant={typeFilter ? 'outline' : 'blue'}
                className="cursor-pointer font-normal"
              >
                <button type="button" onClick={() => setTypeFilter('')}>
                  {t('targeting.location.allTypes', { count: locations.totalHits })}
                </button>
              </Badge>
              {locations.typeCounts.map((bucket) => (
                <Badge
                  key={bucket.type}
                  asChild
                  variant={typeFilter === bucket.type ? 'blue' : 'outline'}
                  className="cursor-pointer font-normal"
                >
                  <button type="button" onClick={() => setTypeFilter(bucket.type)}>
                    {t(`targeting.location.types.${bucket.type}`, { defaultValue: bucket.type })}
                    <span className="tabular-nums opacity-70">{bucket.count}</span>
                  </button>
                </Badge>
              ))}
            </div>
          )}
          {isFetching && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              {t('common.loading')}
            </div>
          )}
          {!isFetching && !locations?.results.length && (
            <p className="text-xs text-muted-foreground">{t('targeting.noResults')}</p>
          )}
          {/* Up to 250 matches, so the list scrolls rather than pushing the
              rest of the editor down the pane. */}
          <div className="flex max-h-72 flex-col overflow-y-auto">
            {locations?.results.map((location) => (
              <button
                key={location.id}
                type="button"
                disabled={targetedLocations.has(location.id)}
                className="flex items-center gap-2 rounded-sm px-1 py-1 text-left text-sm hover:bg-accent disabled:opacity-50"
                onClick={() => addLocation(location)}
              >
                <Plus className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{location.name}</span>
                {location.type && (
                  <Badge variant="outline" className="shrink-0 font-normal">
                    {t(`targeting.location.types.${location.type}`, {
                      defaultValue: location.type,
                    })}
                  </Badge>
                )}
                <span className="truncate text-xs text-muted-foreground">{location.context}</span>
              </button>
            ))}
          </div>
          {!!locations?.results.length && (
            <div className="flex items-center justify-between gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={untargeted.length === 0}
                onClick={() => setConfirmAddAll(true)}
              >
                <Plus className="size-3.5" />
                {t('targeting.location.addAll', { count: untargeted.length })}
              </Button>
              {locations.totalHits > locations.results.length && (
                <p className="text-xs text-muted-foreground">
                  {t('targeting.location.moreResults', {
                    shown: locations.results.length,
                    total: locations.totalHits,
                  })}
                </p>
              )}
            </div>
          )}

          <AlertDialog open={confirmAddAll} onOpenChange={setConfirmAddAll}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('targeting.location.addAllTitle', { count: untargeted.length })}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t('targeting.location.addAllConfirm', { count: untargeted.length })}
                </AlertDialogDescription>
              </AlertDialogHeader>

              <div className="flex max-h-72 flex-col gap-1 overflow-y-auto rounded-md border p-2">
                {untargeted.map((location) => (
                  <div key={location.id} className="flex items-center gap-2 text-sm">
                    <span className="truncate">{location.name}</span>
                    {location.type && (
                      <Badge variant="outline" className="shrink-0 font-normal">
                        {t(`targeting.location.types.${location.type}`, {
                          defaultValue: location.type,
                        })}
                      </Badge>
                    )}
                    <span className="truncate text-xs text-muted-foreground">
                      {location.context}
                    </span>
                  </div>
                ))}
              </div>

              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction onClick={addAllResults}>
                  {t('targeting.location.addAll', { count: untargeted.length })}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      )}

      {!!country && (
        <div className="flex flex-col gap-2">
          <FieldLabel className="text-xs font-medium">
            {t('targeting.location.municipalities')}
          </FieldLabel>
          <Input
            value={municipalityFilter}
            onChange={(e) => setMunicipalityFilter(e.target.value)}
            placeholder={t('targeting.location.filterMunicipalities')}
          />
          {loadingMunicipalities ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Loader2 className="size-3.5 animate-spin" />
              {t('common.loading')}
            </div>
          ) : (
            <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
              {matchingMunicipalities.length === 0 && (
                <p className="text-xs text-muted-foreground">{t('targeting.noResults')}</p>
              )}
              {matchingMunicipalities.map((municipality) => (
                <Badge
                  key={municipality.id}
                  variant={targetedMunicipalities.has(municipality.id) ? 'blue' : 'outline'}
                  asChild
                  className="cursor-pointer font-normal"
                >
                  <button
                    type="button"
                    onClick={() =>
                      onChange({
                        ...value,
                        municipalities: targetedMunicipalities.has(municipality.id)
                          ? value.municipalities.filter((m) => m.id !== municipality.id)
                          : [...value.municipalities, municipality],
                      })
                    }
                  >
                    {municipality.name}
                  </button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <FieldLabel className="text-xs font-medium">
            {t('targeting.location.targeted')}
          </FieldLabel>
          {(value.locations.length > 0 || value.municipalities.length > 0) && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => onChange({ ...value, locations: [], municipalities: [] })}
            >
              {t('targeting.removeAll')}
            </Button>
          )}
        </div>
        {value.locations.length === 0 && value.municipalities.length === 0 ? (
          <p className="rounded-md border border-dashed p-3 text-xs text-muted-foreground">
            {t('targeting.location.empty')}
          </p>
        ) : (
          <ul className="divide-y rounded-md border">
            {value.locations.map((location) => (
              <li key={`l-${location.id}`} className="flex items-center gap-2 px-3 py-2">
                <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {location.name}
                  {location.context && (
                    <span className="text-xs text-muted-foreground"> — {location.context}</span>
                  )}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('common.remove')}
                  onClick={() =>
                    onChange({
                      ...value,
                      locations: value.locations.filter((l) => l.id !== location.id),
                    })
                  }
                >
                  <X className="size-4" />
                </Button>
              </li>
            ))}
            {value.municipalities.map((municipality) => (
              <li key={`m-${municipality.id}`} className="flex items-center gap-2 px-3 py-2">
                <MapPin className="size-3.5 shrink-0 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate text-sm">
                  {municipality.name ?? municipality.id}
                  <span className="text-xs text-muted-foreground">
                    {' '}
                    — {t('targeting.location.municipality')}
                  </span>
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label={t('common.remove')}
                  onClick={() =>
                    onChange({
                      ...value,
                      municipalities: value.municipalities.filter((m) => m.id !== municipality.id),
                    })
                  }
                >
                  <X className="size-4" />
                </Button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
